import { Routes } from '@angular/router';
import { UserRole } from './core/enums/user-role.enum';
import { authGuard } from './core/guards/auth.guard';
import { roleGuard } from './core/guards/role.guard';

export const routes: Routes = [
  // ─── Public Route: Login ───
  {
    path: 'auth/login',
    loadComponent: () =>
      import('./pages/auth/login/login.component').then((m) => m.LoginComponent),
    title: 'Iniciar Sesión | Vuelos Baratos YA',
  },
  {
    path: 'firma-legal/:token',
    loadComponent: () => import('./pages/legal/legal-flow.component').then((m) => m.LegalFlowComponent),
    title: 'Firma Legal | Vuelos Baratos YA',
  },

  // ─── Protected Routes (require authentication) ───
  {
    path: '',
    loadComponent: () =>
      import('./shared/layout/app-layout/app-layout.component').then((m) => m.AppLayoutComponent),
    canActivate: [authGuard],
    children: [
      // Dashboard
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.component').then((m) => m.DashboardComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING, UserRole.EMISSIONS, UserRole.AGENT, UserRole.ANALYST] },
        title: 'Dashboard | Vuelos Baratos YA',
      },
      // Leads
      {
        path: 'leads',
        loadChildren: () => import('./pages/leads/leads.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.AGENT] },
      },
      // Quotes
      {
        path: 'quotes',
        loadChildren: () => import('./pages/quotes/quotes.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.AGENT] },
      },
      // Dossiers
      {
        path: 'dossiers',
        loadChildren: () => import('./pages/dossiers/dossiers.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.AGENT, UserRole.ACCOUNTING, UserRole.EMISSIONS] },
      },
      // Emissions
      {
        path: 'emissions',
        loadChildren: () => import('./pages/emissions/emissions.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.EMISSIONS] },
      },
      // Finance / Treasury & Commissions & Payables
      {
        path: 'finance',
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        children: [
          {
            path: 'treasury',
            loadComponent: () => import('./features/finance/cash-management/cash-management.component').then((m) => m.CashManagementComponent),
            title: 'Control de Efectivo | Vuelos Baratos YA',
          },
          {
            path: 'commissions',
            loadComponent: () => import('./features/finance/commissions-payout/commissions-payout.component').then((m) => m.CommissionsPayoutComponent),
            title: 'Pago de Comisiones | Vuelos Baratos YA',
          },
          {
            path: 'payables',
            loadComponent: () => import('./features/finance/payables/payables.component').then((m) => m.PayablesComponent),
            title: 'Cuentas por Pagar | Vuelos Baratos YA',
          },
          {
            path: 'ledger',
            loadComponent: () => import('./pages/finance/ledger-dashboard/ledger-dashboard.component').then(m => m.LedgerDashboardComponent),
            title: 'Libro Mayor | Vuelos Baratos YA'
          },
          {
            path: 'inbox',
            loadComponent: () => import('./features/finance/ledger-inbox/ledger-inbox.component').then(m => m.LedgerInboxComponent),
            title: 'Bandeja Contable | Vuelos Baratos YA'
          }
        ]
      },
      // Profile / Wallet (All Authenticated Users)
      {
        path: 'profile',
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.AGENT, UserRole.ACCOUNTING, UserRole.EMISSIONS, UserRole.CENTRAL, UserRole.ANALYST] },
        children: [
          {
            path: 'wallet',
            loadComponent: () => import('./features/profile/my-wallet/agent-wallet.component').then((m) => m.AgentWalletComponent),
            canActivate: [roleGuard],
            data: { roles: [UserRole.ADMIN, UserRole.AGENT, UserRole.ACCOUNTING] },
            title: 'Mi Billetera | Vuelos Baratos YA',
          },
          {
            path: 'settings',
            loadComponent: () => import('./pages/profile/notification-settings/notification-settings.component').then((m) => m.NotificationSettingsComponent),
            title: 'Preferencias de Notificación | Vuelos Baratos YA',
          }
        ]
      },

      // Passengers (CRM)
      {
        path: 'passengers',
        loadChildren: () =>
          import('./pages/passengers/passengers.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING, UserRole.AGENT, UserRole.EMISSIONS, UserRole.CENTRAL] },
      },
      // Providers Management (ADMIN, ACCOUNTING)
      {
        path: 'admin/providers',
        loadComponent: () => import('./features/admin/providers-management/providers-management.component').then((m) => m.ProvidersManagementComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Gestión de Proveedores | Vuelos Baratos YA',
      },
      {
        path: 'admin/providers/:id',
        loadComponent: () => import('./features/admin/provider-detail/provider-detail.component').then((m) => m.ProviderDetailComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Detalle de Proveedor | Vuelos Baratos YA',
      },
      // Airlines Management (ADMIN, ACCOUNTING)
      {
        path: 'admin/airlines',
        loadComponent: () => import('./features/admin/airlines-management/airlines-management.component').then((m) => m.AirlinesManagementComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Gestión de Aerolíneas | Vuelos Baratos YA',
      },
      // Airports and cities catalog (ADMIN writes; ACCOUNTING reads)
      {
        path: 'admin/airports',
        loadComponent: () => import('./features/admin/airports-management/airports-management.component').then((m) => m.AirportsManagementComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Aeropuertos y Ciudades | Vuelos Baratos YA',
      },
      // Payment Methods Management (ADMIN, ACCOUNTING)
      {
        path: 'admin/payment-methods',
        loadComponent: () => import('./features/admin/payment-methods-management/payment-methods-management.component').then((m) => m.PaymentMethodsManagementComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Métodos de Pago | Vuelos Baratos YA',
      },
      // Website Pricing Rules (ADMIN writes; ACCOUNTING reads and simulates)
      {
        path: 'admin/pricing-rules',
        loadComponent: () => import('./features/admin/pricing-rules/pricing-rules.component').then((m) => m.PricingRulesComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Reglas de Precio | Vuelos Baratos YA',
      },
      // Website Promotions (ADMIN writes; ANALYST reads). 'new' goes before ':id'.
      {
        path: 'admin/promotions',
        loadComponent: () => import('./features/admin/promotions/promotions-list.component').then((m) => m.PromotionsListComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST] },
        title: 'Promociones | Vuelos Baratos YA',
      },
      {
        path: 'admin/promotions/new',
        loadComponent: () => import('./features/admin/promotions/promotion-form.component').then((m) => m.PromotionFormComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Nueva Promoción | Vuelos Baratos YA',
      },
      {
        path: 'admin/promotions/:id',
        loadComponent: () => import('./features/admin/promotions/promotion-form.component').then((m) => m.PromotionFormComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST] },
        title: 'Promoción | Vuelos Baratos YA',
      },
      // Public Website Parameters (ADMIN writes; ACCOUNTING reads)
      {
        path: 'admin/site-config',
        loadComponent: () => import('./features/admin/public-site-config/public-site-config.component').then((m) => m.PublicSiteConfigComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Parámetros del Sitio | Vuelos Baratos YA',
      },
      // Website Content: articles and experiences (ADMIN writes; ANALYST reads)
      {
        path: 'admin/content',
        pathMatch: 'full',
        redirectTo: 'admin/content/articles',
      },
      {
        path: 'admin/content/articles',
        loadComponent: () => import('./features/admin/content/content-list.component').then((m) => m.ContentListComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST], kind: 'articles' },
        title: 'Artículos | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/articles/new',
        loadComponent: () => import('./features/admin/content/article-editor.component').then((m) => m.ArticleEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Nuevo artículo | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/articles/:id',
        loadComponent: () => import('./features/admin/content/article-editor.component').then((m) => m.ArticleEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST] },
        title: 'Artículo | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/experiences',
        loadComponent: () => import('./features/admin/content/content-list.component').then((m) => m.ContentListComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST], kind: 'experiences' },
        title: 'Experiencias | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/experiences/new',
        loadComponent: () => import('./features/admin/content/experience-editor.component').then((m) => m.ExperienceEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Nueva experiencia | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/experiences/:id',
        loadComponent: () => import('./features/admin/content/experience-editor.component').then((m) => m.ExperienceEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST] },
        title: 'Experiencia | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/route-hubs',
        loadComponent: () => import('./features/admin/content/content-list.component').then((m) => m.ContentListComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST], kind: 'route-hubs' },
        title: 'Páginas de ruta | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/route-hubs/new',
        loadComponent: () => import('./features/admin/content/route-hub-editor.component').then((m) => m.RouteHubEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Nueva página de ruta | Vuelos Baratos YA',
      },
      {
        path: 'admin/content/route-hubs/:id',
        loadComponent: () => import('./features/admin/content/route-hub-editor.component').then((m) => m.RouteHubEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST] },
        title: 'Página de ruta | Vuelos Baratos YA',
      },
      // SEO: keyword map (ADMIN writes; ANALYST reads) and Instagram → drafts (ADMIN)
      {
        path: 'admin/seo/keywords',
        loadComponent: () => import('./features/admin/seo/keywords.component').then((m) => m.SeoKeywordsComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ANALYST] },
        title: 'Palabras clave | Vuelos Baratos YA',
      },
      {
        path: 'admin/seo/ingest',
        loadComponent: () => import('./features/admin/seo/instagram-ingest.component').then((m) => m.InstagramIngestComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Desde Instagram | Vuelos Baratos YA',
      },
      // Requisitos de viaje: reglas del verificador público (ADMIN/EMISSIONS escriben; ANALYST lee)
      {
        path: 'admin/requirements/rules',
        loadComponent: () => import('./features/admin/requirements/requirement-rules.component').then((m) => m.RequirementRulesComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.EMISSIONS, UserRole.ANALYST] },
        title: 'Reglas de requisitos | Vuelos Baratos YA',
      },
      {
        path: 'admin/requirements/rules/new',
        loadComponent: () => import('./features/admin/requirements/requirement-rule-editor.component').then((m) => m.RequirementRuleEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.EMISSIONS] },
        title: 'Reglas de requisitos | Vuelos Baratos YA',
      },
      {
        path: 'admin/requirements/rules/:id',
        loadComponent: () => import('./features/admin/requirements/requirement-rule-editor.component').then((m) => m.RequirementRuleEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.EMISSIONS, UserRole.ANALYST] },
        title: 'Reglas de requisitos | Vuelos Baratos YA',
      },
      // Branches Governance & Metrics (ADMIN, ACCOUNTING)
      {
        path: 'admin/branches',
        loadComponent: () => import('./features/admin/branches/branches-management.component').then((m) => m.BranchesManagementComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.ACCOUNTING] },
        title: 'Gobernanza de Sucursales | Vuelos Baratos YA',
      },
      // Users Management (Nested Routes: /users, /users/new, /users/:id)
      {
        path: 'users',
        loadChildren: () => import('./pages/users/users.routes').then((m) => m.routes),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.CENTRAL] },
      },
      // Audit Logs (ADMIN only)
      {
        path: 'audit',
        loadComponent: () => import('./pages/audit/audit-log/audit-log.component').then((m) => m.AuditLogComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Logs de Auditoría | Vuelos Baratos YA',
      },
      // Customer Service (Settings)
      {
        path: 'customer-service/settings',
        loadComponent: () => import('./pages/customer-service/settings/customer-service-settings.component').then((m) => m.CustomerServiceSettingsComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Configuración de Atención al Cliente | Vuelos Baratos YA',
      },
      // Customer Service (Radar)
      {
        path: 'customer-service/radar',
        loadComponent: () => import('./pages/customer-service/radar/sla-monitor.component').then((m) => m.SlaMonitorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.CENTRAL] },
        title: 'Radar SLA de Atención al Cliente | Vuelos Baratos YA',
      },
      // Customer Service (Agentes — B3 SLA v2.1)
      {
        path: 'customer-service/agents',
        loadComponent: () => import('./pages/customer-service/agents/agent-monitor.component').then((m) => m.AgentMonitorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.CENTRAL] },
        title: 'Monitor de Agentes | Vuelos Baratos YA',
      },
      // Customer Service (AI Editor)
      {
        path: 'customer-service/ai-editor',
        loadComponent: () => import('./pages/customer-service/ai-editor/ai-prompt-editor.component').then((m) => m.AiPromptEditorComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN] },
        title: 'Editor de Prompt IA | Vuelos Baratos YA',
      },
      // Customer Service (Comentarios IG/FB)
      {
        path: 'customer-service/social-comments',
        loadComponent: () => import('./pages/customer-service/social-comments/social-comments.component').then((m) => m.SocialCommentsComponent),
        canActivate: [roleGuard],
        data: { roles: [UserRole.ADMIN, UserRole.CENTRAL] },
        title: 'Comentarios IG/FB | Vuelos Baratos YA',
      },
      // Default redirect inside layout
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
    ],
  },

  // ─── Wildcard: redirect to dashboard ───
  {
    path: '**',
    redirectTo: 'dashboard',
  },
];
